import { HYPE_STAKED_ADDRESS_HYPERCORE } from '@/constants/addresses';

import type { PerpAnnotation } from './types';

/**
 * HIP-3 coins are named `{dex}:{coin}`; validator-operated perps and spot coins have no
 * prefix and belong to dex `''`.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/asset-ids
 */
export const dexOfCoin = (coin: string) => coin.slice(0, Math.max(0, coin.indexOf(':')));

/** The coin without its dex prefix, for display next to a deployer tag. */
export const coinLabel = (coin: string) => coin.slice(coin.indexOf(':') + 1);

/**
 * The deployer's `displayName` when annotated (`io:OAI` → `OPENAI`, `xyz:SP500` → `S&P500`),
 * else `coinLabel`. Display only: requests, routes, favorites, and icons keep the L1 coin,
 * and the name is free text, so never put it in a URL or filename.
 */
export const perpCoinLabel = (annotation: PerpAnnotation | undefined, coin: string) =>
  annotation?.displayName || coinLabel(coin);

/**
 * Spot pairs are `PURR/USDC` or `@{spotIndex}`; they reach us through account feeds
 * (fills, open orders) when the user trades spot outside Matcha.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#perpetuals-vs-spot
 */
export const isSpotCoin = (coin: string) => coin.startsWith('@') || coin.includes('/');

/**
 * HIP-4 outcome (prediction market) coins are `#{10 * outcome + side}`. Settled outcomes
 * drop out of `outcomeMeta`, so there is no way to name them after the fact.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/asset-ids#outcomes
 */
export const isOutcomeCoin = (coin: string) => coin.startsWith('#');

/**
 * Icons come from the official Hyperliquid frontend's /coins assets.
 * Unnamespaced multiplied contracts drop the leading k (kPEPE → PEPE.svg).
 * HIP-3 names retain the full DEX prefix and coin name (km:US500, xyz:kPEPE).
 */
export function getPerpsCoinIconUrl(coin: string): string {
  const iconName = coin.includes(':') ? coin : coin.replace(/^k/, '');
  return `https://app.hyperliquid.xyz/coins/${encodeURIComponent(iconName)}.svg`;
}

/**
 * Spot tokens whose icon (and name in the official frontend) is the asset they wrap, keyed by
 * L1 token name. Lifted from app.hyperliquid.xyz's mainnet alias table; testnet names differ.
 */
const SPOT_ICON_ALIASES: Record<string, string> = {
  UBTC: 'BTC',
  UETH: 'ETH',
  USOL: 'SOL',
  UFART: 'FARTCOIN',
  USDT0: 'USDT',
  HPENGU: 'PENGU',
  XAUT0: 'XAUT',
  UPUMP: 'PUMP',
  PUMP: 'PUMP-26',
  ANSEM: 'ANSEM-19',
  UANSEM: 'ANSEM',
  UUUSPX: 'SPX',
  UBONK: 'BONK',
  WMNT: 'MNT',
  UXPL: 'XPL',
  UDZ: '2Z',
  UENA: 'ENA',
  MON: 'MONPRO',
  UMON: 'MON',
  MMOVE: 'MOVE',
  UZEC: 'ZEC',
  HSEI: 'SEI',
  UVIRT: 'VIRTUAL',
  UAVAX: 'AVAX',
  ONEAR: 'NEAR',
  HNIL: 'NIL',
};

/**
 * Spot token icons live beside the perps ones with a `_spot` suffix (HYPE_spot.svg). Missing
 * icons come back as the app's HTML with a 200, so callers must rely on the image's error event.
 */
export function getHyperCoreSpotIconUrl(tokenName: string): string {
  return `https://app.hyperliquid.xyz/coins/${encodeURIComponent(hyperCoreSpotIconName(tokenName))}_spot.svg`;
}

/** The asset a token row's icon depicts: the underlying for wrapped and staked tokens, else itself. */
export const hyperCoreSpotIconName = (tokenName: string) =>
  tokenName === HYPE_STAKED_ADDRESS_HYPERCORE
    ? 'HYPE'
    : (SPOT_ICON_ALIASES[tokenName] ?? tokenName);
